import { twMerge } from "tailwind-merge";
import { Skeleton } from "@components/ui/skeleton";
import { usePage } from "@inertiajs/react";

interface AdPreviewProps {
  className?: string;
  headlines?: Array<{ text: string }>;
  descriptions?: Array<{ text: string }>;
  callouts?: Array<{ text: string }>;
  websiteUrl?: string;
  // When provided, callers signal "data is loaded — show this label instead of skeletons if creative is empty"
  emptyLabel?: string;
}

interface AdPreviewPageProps {
  headlines?: Array<{ text: string }>;
  descriptions?: Array<{ text: string }>;
  callouts?: Array<{ text: string }>;
  website_url?: string;
  [key: string]: unknown;
}

export default function AdPreview({
  className,
  headlines: headlinesProp,
  descriptions: descriptionsProp,
  callouts: calloutsProp,
  websiteUrl: websiteUrlProp,
  emptyLabel,
}: AdPreviewProps) {
  const page = usePage<AdPreviewPageProps>();
  const headlines = headlinesProp ?? page.props.headlines;
  const descriptions = descriptionsProp ?? page.props.descriptions;
  const callouts = calloutsProp ?? page.props.callouts;
  const websiteUrl = websiteUrlProp ?? page.props.website_url;
  const showEmptyState = emptyLabel !== undefined && (headlines?.length ?? 0) === 0;

  const previewHeadline =
    headlines
      ?.slice(0, 3)
      .map((h) => h.text)
      .join(" | ") || "";

  const previewDescription = descriptions?.[0]?.text || "";

  const previewCallouts = callouts?.slice(0, 4) || [];

  return (
    <div
      className={twMerge(
        "w-full bg-white rounded-xl py-8 pl-10 pr-16 relative overflow-hidden",
        className
      )}
    >
      <div 
        className="absolute inset-0 rounded-[inherit] border border-neutral-200/50 pointer-events-none"
        style={{ backgroundImage: "linear-gradient(180deg, rgba(237, 237, 236, 0.2) 0%, rgba(237, 237, 236, 0) 100%)" }}
      />
      <div className="absolute top-6 right-8 text-base-400 text-sm">Ad Preview</div>
      <div className="max-w-[calc(100%-100px)]">
        <div className="flex flex-col mb-2 gap-2">
          {showEmptyState ? (
            <h2 className="text-lg font-medium text-base-400 italic font-roboto break-words">
              {emptyLabel}
            </h2>
          ) : !previewHeadline ? (
            <Skeleton className="w-52 h-6" />
          ) : (
            <h2 className="text-xl font-medium text-primary-500 font-roboto break-words">{previewHeadline}</h2>
          )}
          {!websiteUrl ? (
            showEmptyState ? null : <Skeleton className="w-32 h-4" />
          ) : (
            <span className="text-xs text-success-700">{websiteUrl}</span>
          )}
        </div>
        {!showEmptyState && (
          <div className="flex flex-col gap-3">
            <div className="text-sm text-base-400 leading-[18px] break-words">
              {!previewDescription ? <Skeleton className="w-full h-12" /> : previewDescription}
            </div>
            <div className="flex gap-6 flex-wrap">
              {previewCallouts.length > 0 ? (
                previewCallouts.map((c, i) => (
                  <span key={i} className="text-sm text-primary-500">{c.text}</span>
                ))
              ) : (
                <>
                  <Skeleton className="w-20 h-4" />
                  <Skeleton className="w-24 h-4" />
                  <Skeleton className="w-20 h-4" />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
